import { Draft } from './Draft';
import type { Risk } from './Risk';
import {
  TripType,
  GeographicalArea,
  FamilySituation,
  Variant,
} from '@qover-private/protos-web/qover/dojo/travel/v1/terms';
import { PaymentRythm } from './PaymentRythm';
import PriceInfo from './PriceInfo';

const formatPrice = (price: number) => (price / 100).toFixed(2);

const defineOption = (option: { name: string; category: string }) => ({
  ...option,
  brand: 'option',
  dimension1: 'option',
});

const BAGGAGE = defineOption({
  name: 'option baggages',
  category: 'baggages',
});
const ACCIDENT = defineOption({
  name: 'option accident',
  category: 'accident',
});
const CANCELLATION = defineOption({
  name: 'option trip cancellation',
  category: 'trip cancellation',
});

const typeToLabel = {
  [TripType.TRIP_TYPE_MULTI_TRIP]: 'less than 6 months',
  [TripType.TRIP_TYPE_YEAR_TRIP]: 'more than 6 months',
  [GeographicalArea.GEOGRAPHICAL_AREA_EUROPE]: 'europe',
  [GeographicalArea.GEOGRAPHICAL_AREA_WORLD]: 'world',
  [GeographicalArea.GEOGRAPHICAL_AREA_UNSPECIFIED]: '',
  [Variant.VARIANT_STANDARD]: 'standard',
  [Variant.VARIANT_PREMIUM]: 'premium',
  [Variant.VARIANT_UNSPECIFIED]: '',
};

const typeToFamilySituation = {
  [TripType.TRIP_TYPE_SINGLE_TRIP]: {
    [FamilySituation.FAMILY_SITUATION_SINGLE]: 'single',
    [FamilySituation.FAMILY_SITUATION_COUPLE]: 'single',
    [FamilySituation.FAMILY_SITUATION_FAMILY]: 'single',
    [FamilySituation.FAMILY_SITUATION_UNSPECIFIED]: '',
  },
  [TripType.TRIP_TYPE_MULTI_TRIP]: {
    [FamilySituation.FAMILY_SITUATION_SINGLE]: 'single',
    [FamilySituation.FAMILY_SITUATION_COUPLE]: 'couple',
    [FamilySituation.FAMILY_SITUATION_FAMILY]: 'family',
    [FamilySituation.FAMILY_SITUATION_UNSPECIFIED]: '',
  },
  [TripType.TRIP_TYPE_YEAR_TRIP]: {
    [FamilySituation.FAMILY_SITUATION_SINGLE]: 'single',
    [FamilySituation.FAMILY_SITUATION_COUPLE]: 'couple',
    [FamilySituation.FAMILY_SITUATION_FAMILY]: 'family',
    [FamilySituation.FAMILY_SITUATION_UNSPECIFIED]: '',
  },
  [TripType.TRIP_TYPE_UNSPECIFIED]: {
    [FamilySituation.FAMILY_SITUATION_SINGLE]: '',
    [FamilySituation.FAMILY_SITUATION_COUPLE]: '',
    [FamilySituation.FAMILY_SITUATION_FAMILY]: '',
    [FamilySituation.FAMILY_SITUATION_UNSPECIFIED]: '',
  },
};

const typeToBrand = {
  [TripType.TRIP_TYPE_SINGLE_TRIP]: 'single trip',
  [TripType.TRIP_TYPE_MULTI_TRIP]: 'multi-trips',
  [TripType.TRIP_TYPE_YEAR_TRIP]: 'multi-trips',
  [TripType.TRIP_TYPE_UNSPECIFIED]: '',
};

const pushGetAQuote = (action: string, label: string) => {
  // @ts-ignore
  dataLayer.push({
    event: 'getToQuote',
    eventCategory: 'get to quote',
    eventAction: action,
    eventLabel: label,
  });
};

export const pushGetAQuoteSingle = () =>
  pushGetAQuote('01 - variant type', 'variant single-trip selected');
export const pushGetAQuoteMulti = () =>
  pushGetAQuote('01 - variant type', 'variant multi-trips selected');

export const pushGetAQuoteTripDuration = (draft: Draft) => {
  const tripDuration = Draft.getTripDuration(draft);

  let label: string;

  if (tripDuration <= 25) {
    label = 'duration up to 25 days';
  } else if (tripDuration <= 90) {
    label = 'duration 26-90 days';
  } else if (tripDuration <= 180) {
    label = 'duration 91-180 days';
  } else {
    label = 'duration >180 days';
  }

  pushGetAQuote('02 single-trip - duration validated', label);
};

export const pushGetAQuoteMaxTripDuration = (draft: Draft) => {
  const tripType = draft.terms?.tripType;
  if (
    tripType === TripType.TRIP_TYPE_MULTI_TRIP ||
    tripType == TripType.TRIP_TYPE_YEAR_TRIP
  ) {
    pushGetAQuote('02 multi-trips - duration validated', typeToLabel[tripType]);
  }
};

export const pushGetAQuoteGeoTrip = (draft: Draft) => {
  const geoArea = draft.terms.geographicalArea;
  if (
    geoArea === GeographicalArea.GEOGRAPHICAL_AREA_EUROPE ||
    geoArea === GeographicalArea.GEOGRAPHICAL_AREA_WORLD
  ) {
    pushGetAQuote('03 single - geographic area selected', typeToLabel[geoArea]);
  }
};

export const pushUpdatePlan = () => {
  // @ts-ignore
  dataLayer.push({
    event: 'updatePlanEvent',
    eventCategory: 'Update',
    eventAction: 'opened',
  });
};

const createProduct = (draft: Draft, product: any, price: number) => ({
  ...product,
  price: formatPrice(price),
  quantity:
    (draft.risk?.adultsList?.length ?? 0) +
    (draft.risk?.childrenList?.length ?? 0),
});

const createBaggage = (draft: Draft, price: number) =>
  createProduct(draft, BAGGAGE, price);

const createAccident = (draft: Draft, price: number) =>
  createProduct(draft, ACCIDENT, price);

const createCancellation = (draft: Draft, price: number) =>
  createProduct(draft, CANCELLATION, price);

type Prices = {
  accident: number;
  baggage: number;
  cancellation: number;
  travel: number;
};

const createAllOptions = (draft: Draft, prices?: Prices) => {
  let options = [];

  const travelPrice =
    prices?.travel ?? draft.getTravelPrice(PaymentRythm.YEARLY);

  if (draft.terms?.isSingleTrip() && draft.terms.isInEurope()) {
    options.push(productSingleTripEurope(draft, travelPrice));
  } else if (draft.terms?.isSingleTrip()) {
    options.push(productSingleTripWorld(draft, travelPrice));
  } else if (draft.terms?.isMultiTrip() && draft.terms.isMultiTripPremium()) {
    options.push(productMultiTripPremium(draft, travelPrice));
  } else if (draft.terms?.isMultiTrip()) {
    options.push(productMultiTripStandard(draft, travelPrice));
  } else {
    options.push(productYearTrip(draft, travelPrice));
  }

  if (draft.terms?.hasBaggageProtection) {
    options.push(
      createBaggage(
        draft,
        prices?.baggage ?? draft.getBaggagePrice(PaymentRythm.YEARLY),
      ),
    );
  }

  if (draft.terms?.hasAccidentProtection) {
    options.push(
      createAccident(
        draft,
        prices?.accident ?? draft.getAccidentPrice(PaymentRythm.YEARLY),
      ),
    );
  }

  if (draft.terms?.hasCancellationTrip) {
    options.push(
      createCancellation(
        draft,
        prices?.cancellation ?? draft.getCancellationPrice(PaymentRythm.YEARLY),
      ),
    );
  }

  return options;
};

const createOptionsView = (draft: Draft, prices: Prices) => [
  createBaggage(draft, prices.baggage),
  createAccident(draft, prices.accident),
  createCancellation(draft, prices.cancellation),
];

export const pushProductDetailsOptionView = (draft: Draft, prices: Prices) => {
  const options = createOptionsView(draft, prices);
  // @ts-ignore
  dataLayer.push({
    event: `productDetailOptionView${options.length}`,
    ecommerce: {
      currencyCode: draft.settings?.currency,
      detail: { products: options },
    },
  });
};

const handleOptionToCart = (hasOption: boolean, draft: Draft, product: any) =>
  // @ts-ignore
  dataLayer.push({
    event: hasOption ? 'removeFromCartOption' : 'addToCartOption',
    ecommerce: {
      currencyCode: draft.settings?.currency,
      [hasOption ? 'remove' : 'add']: {
        products: [product],
      },
    },
  });

export const pushHandleBaggageOptionToCart = (draft: Draft, price: number) =>
  handleOptionToCart(
    !!draft.terms?.hasBaggageProtection,
    draft,
    createBaggage(draft, price),
  );

export const pushHandleAccidentOptionToCart = (draft: Draft, price: number) =>
  handleOptionToCart(
    !!draft.terms?.hasAccidentProtection,
    draft,
    createAccident(draft, price),
  );

export const pushHandleCancellationOptionToCart = (
  draft: Draft,
  price: number,
) =>
  handleOptionToCart(
    !!draft.terms?.hasCancellationTrip,
    draft,
    createCancellation(draft, price),
  );

export const pushUpdateCancellationOptionToCart = (
  draft: Draft,
  price: number,
) => handleOptionToCart(false, draft, createCancellation(draft, price));

const pushCheckout = (draft: Draft, step: number) => {
  // @ts-ignore
  dataLayer.push({
    event: `checkout${step}`,
    ecommerce: {
      currencyCode: draft.settings?.currency,
      checkout: {
        actionField: { step },
        products: createAllOptions(draft),
      },
    },
  });
};

const pushCheckoutOptions = (
  draft: Draft,
  step: number,
  priceInfo: PriceInfo,
) => {
  const optionsPrice = priceInfo?.getOptionsFromDraft(draft);

  const paymentRythm =
    draft?.terms?.tripType === TripType.TRIP_TYPE_SINGLE_TRIP
      ? PaymentRythm.YEARLY
      : PaymentRythm.MONTHLY;

  const prices = optionsPrice.getPrices(
    paymentRythm,
    draft?.terms?.cancellationClaimAmount,
  );

  // @ts-ignore
  dataLayer.push({
    event: `checkout${step}`,
    ecommerce: {
      currencyCode: draft.settings?.currency,
      checkout: {
        actionField: { step },
        products: createAllOptions(draft, prices),
      },
    },
  });
};

export const pushOptionCheckout = (draft: Draft, prices: PriceInfo) =>
  pushCheckoutOptions(draft, 1, prices);
export const pushInsuredCheckout = (draft: Draft) => pushCheckout(draft, 2);
export const pushPolicyholderCheckout = (draft: Draft) =>
  pushCheckout(draft, 3);
export const pushSummaryCheckoutDesktop = (draft: Draft) => {
  pushCheckout(draft, 4);
  pushCheckout(draft, 5);
};
export const pushPaymentCheckoutDesktop = (draft: Draft) => {
  pushCheckout(draft, 6);
};

export const pushPurchase = (draft: Draft, contract: any) => {
  const prices = {
    travel: contract?.coverages.find((c: any) => c.name === 'TRAVEL')?.priceInfo
      ?.yearlyPremium?.withTaxes,
    baggage: contract?.coverages.find((c: any) => c.name === 'BAGGAGE')
      ?.priceInfo?.yearlyPremium?.withTaxes,
    accident: contract?.coverages.find((c: any) => c.name === 'ACCIDENT')
      ?.priceInfo?.yearlyPremium?.withTaxes,
    cancellation: contract?.coverages.find(
      (c: any) => c.name === contract.terms.cancellationClaimAmount,
    )?.priceInfo?.totalPrice?.withTaxes,
  };

  // @ts-ignore
  dataLayer.push({
    event: 'ecommerce',
    ecommerce: {
      currencyCode: contract.settings.currency,
      purchase: {
        actionField: {
          id: contract.contractId,
          affiliation: contract?.payment?.paymentMethod
            .substr(15)
            .toLowerCase(),
          revenue: formatPrice(
            contract?.totalCoverage?.priceInfo?.yearlyPremium?.withTaxes,
          ),
          tax: formatPrice(
            contract?.totalCoverage?.priceInfo?.yearlyPremium?.totalTaxes,
          ),
        },
        products: createAllOptions(draft, prices),
      },
    },
  });
};

const eventToLabel = (e: any) => (e.target.open ? 'expand' : 'collapse');

export const pushQuoteSections = (eventAction: string, eventLabel: string) => {
  // @ts-ignore
  dataLayer.push({
    event: 'quoteSections',
    eventCategory: 'quote sections',
    eventAction,
    eventLabel,
  });
};

export const pushQuoteSectionsGuarantee = (e: any) =>
  pushQuoteSections('guarantees', eventToLabel(e));

export const pushQuoteSectionsOptions = (e: any) =>
  pushQuoteSections('options', eventToLabel(e));

export const pushQuoteSectionsGeographicCoverage = (e: any) =>
  pushQuoteSections('Geographic coverage', eventToLabel(e));

export const pushQuoteSectionsExclusions = (e: any) =>
  pushQuoteSections('exclusions', eventToLabel(e));

export const pushQuoteSectionsEligibility = (e: any) =>
  pushQuoteSections('eligibility rules', eventToLabel(e));

export const pushQuoteSectionsClaim = (e: any) =>
  pushQuoteSections('how to file a claim', eventToLabel(e));

export const pushQuoteSectionsInsurance = (e: any) =>
  pushQuoteSections('insurance documents', eventToLabel(e));

export const pushQuoteSectionsCancel = (e: any) =>
  pushQuoteSections('how to cancel', eventToLabel(e));

export const pushQuoteSectionsInsured = (e: any) =>
  pushQuoteSections('already insured', eventToLabel(e));

export const pushQuoteSectionsContact = (e: any) =>
  pushQuoteSections('contact', eventToLabel(e));

export const createMainProduct = (draft: Draft, priceInfo: PriceInfo) => {
  const optionsPrice = priceInfo?.getOptionsFromDraft(draft);

  const tripType = draft.terms?.tripType as TripType;
  const familySituation = Draft.getFamilySituation(draft.risk);
  const tripDuration =
    tripType === TripType.TRIP_TYPE_SINGLE_TRIP
      ? Draft.getTripDuration(draft)
      : 365;

  const variant = typeToFamilySituation[tripType][familySituation];
  const brand = typeToBrand[tripType];

  let category: string = '';

  if (tripType === TripType.TRIP_TYPE_SINGLE_TRIP) {
    const geoArea = draft.terms.geographicalArea;
    category = typeToLabel[geoArea as GeographicalArea];
  } else if (tripType === TripType.TRIP_TYPE_MULTI_TRIP) {
    const tripVariant = draft.terms.variant;
    category = typeToLabel[tripVariant as Variant];
  } else if (tripType === TripType.TRIP_TYPE_YEAR_TRIP) {
    category = 'one year';
  }

  // @ts-ignore
  dataLayer.push({
    event: 'addToCartMain',
    ecommerce: {
      currencyCode: draft.settings?.currency,
      add: {
        actionField: { list: 'update' },
        products: [
          {
            id: '01',
            name: `${brand} ${category} ${tripDuration} days`,
            variant,
            category,
            brand,
            dimension1: 'Main',
            price: formatPrice(optionsPrice.getTravel(PaymentRythm.YEARLY)),
          },
        ],
      },
    },
  });
};

const viewProducts = (draft: Draft, products: any) => {
  // @ts-ignore
  dataLayer.push({
    event: 'productDetailView1', //The event name adapts to the number of plans proposed
    ecommerce: {
      currencyCode: draft.settings?.currency,
      detail: {
        products,
      },
    },
  });
};

const productSingleTripEurope = (form: Draft, price: number) => ({
  id: '01',
  name: `single trip europe ${Draft.getTripDuration(form)} days`,
  category: 'europe',
  variant:
    typeToFamilySituation[TripType.TRIP_TYPE_YEAR_TRIP][
      Draft.getFamilySituation(form.risk)
    ],
  brand: 'single trip',
  dimension1: 'Main',
  price: formatPrice(price),
});

const productSingleTripWorld = (form: Draft, price: number) => ({
  id: '01',
  name: `single trip world ${Draft.getTripDuration(form)} days`,
  category: 'world',
  variant:
    typeToFamilySituation[TripType.TRIP_TYPE_YEAR_TRIP][
      Draft.getFamilySituation(form.risk)
    ],
  brand: 'single trip',
  dimension1: 'Main',
  price: formatPrice(price),
});

const productYearTrip = (form: Draft, price: number) => ({
  id: '01',
  name: 'multi-trips 365 days',
  category: 'one year',
  variant:
    typeToFamilySituation[TripType.TRIP_TYPE_YEAR_TRIP][
      Draft.getFamilySituation(form.risk)
    ],
  brand: 'multi-trips',
  dimension1: 'Main',
  price: formatPrice(price),
});

const productMultiTripPremium = (form: Draft, price: number) => ({
  id: '01',
  name: 'multi-trips 365 days',
  category: 'premium',
  variant:
    typeToFamilySituation[TripType.TRIP_TYPE_MULTI_TRIP][
      Draft.getFamilySituation(form.risk)
    ],
  brand: 'multi-trips',
  dimension1: 'Main',
  price: formatPrice(price),
});

const productMultiTripStandard = (form: Draft, price: number) => ({
  id: '01',
  name: 'multi-trips 365 days',
  category: 'standard',
  variant:
    typeToFamilySituation[TripType.TRIP_TYPE_MULTI_TRIP][
      Draft.getFamilySituation(form.risk)
    ],
  brand: 'multi-trips',
  dimension1: 'Main',
  price: formatPrice(price),
});

export const pushViewSingleTripEurope = (form: Draft, price: number) =>
  viewProducts(form, [productSingleTripEurope(form, price)]);

export const pushViewSingleTripWorld = (form: Draft, price: number) =>
  viewProducts(form, [productSingleTripWorld(form, price)]);

export const pushViewMultiTripPremium = (form: Draft, price: number) =>
  viewProducts(form, [productMultiTripPremium(form, price)]);

export const pushViewMultiTripStandard = (form: Draft, price: number) =>
  viewProducts(form, [productMultiTripStandard(form, price)]);

export const pushViewYearTrip = (form: Draft, price: number) =>
  viewProducts(form, [productYearTrip(form, price)]);
